import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { AccountGroup } from '@galaxy/account-group';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { SearchSelectComponent, UIKitModule } from '@vendasta/uikit';
import { Observable } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { ActionList } from '../../action-lists/action-list';
import { ActionListsApiService } from '../../action-lists/action-lists.api.service';
import { ActionListsStoreService } from '../../action-lists/action-lists.store.service';

enum SelectedTab {
  add = 0,
  create = 1,
}

@Component({
  imports: [
    MatTabsModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslateModule,
    UIKitModule,
  ],
  templateUrl: './add-to-list-dialog.component.html',
  styleUrls: ['./add-to-list-dialog.component.scss'],
})
export class AddToListDialogComponent implements OnInit {
  @Input() accountGroup: AccountGroup;
  @ViewChild('searchSelect') searchSelect: SearchSelectComponent;
  @ViewChild('matTabGroup') matTabGroup: MatTabGroup;

  selectedList: ActionList;
  loading = false;
  form: UntypedFormGroup;
  CREATE_NEW_LIST = 'createNewList';
  selectedIndex = 0;

  constructor(
    public dialogRef: MatDialogRef<AddToListDialogComponent>,
    private snackbarService: SnackbarService,
    public actionListsApiService: ActionListsApiService,
    public actionListsStoreService: ActionListsStoreService,
    private formBuilder: UntypedFormBuilder,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      createNewList: [''],
      existingLists: [''],
    });
  }

  keyPressed(event: KeyboardEvent): void {
    if (event.charCode === 13) {
      // Enter pressed
      this.addAccountToList();
    }
  }

  disableAddButton(): boolean {
    if (this.form.controls[this.CREATE_NEW_LIST].value === '' && !this.selectedList) {
      return true;
    } else {
      return false;
    }
  }

  get submitText(): string {
    if (this.selectedIndex === SelectedTab.create) {
      return this.translate.instant('LISTS.LIST_DIALOG.CREATE_AND_ADD');
    } else {
      return this.translate.instant('LISTS.LIST_DIALOG.ADD');
    }
  }

  addAccountToList(): void {
    if (this.loading) {
      return;
    }
    this.loading = true;

    let addToList$: Observable<any>;
    if (this.form.controls[this.CREATE_NEW_LIST].value !== '' && this.selectedIndex === 1) {
      addToList$ = this.actionListsApiService.createActionList(this.form.controls[this.CREATE_NEW_LIST].value).pipe(
        tap((actionList) => (this.selectedList = actionList)),
        switchMap(() => {
          return this.actionListsApiService.addAccountToActionList(
            this.selectedList.name,
            this.accountGroup.accountGroupId,
          );
        }),
      );
    } else {
      addToList$ = this.actionListsApiService.addAccountToActionList(
        this.selectedList.name,
        this.accountGroup.accountGroupId,
      );
    }

    addToList$.pipe(take(1)).subscribe({
      next: () => {
        this.loading = false;
        this.snackbarService.openSuccessSnack('LISTS.LIST_DIALOG.SUCCESS', {
          interpolateTranslateParams: {
            account: this.accountGroup.napData.companyName,
            list: this.selectedList.name,
          },
        });
        this.dialogRef.close(true);
      },
      error: () => {
        this.loading = false;
        this.snackbarService.openErrorSnack('LISTS.LIST_DIALOG.FAILURE', {
          interpolateTranslateParams: {
            account: this.accountGroup.napData.companyName,
            list: this.selectedList.name,
          },
        });
      },
    });
  }

  goToCreateTab(): void {
    this.matTabGroup.selectedIndex = 1;
  }
}
